import { useEffect, useMemo, useState, useCallback } from 'react';
import { CityPicker } from '../components/CityPicker';
import { AmbientShell } from '../components/AmbientShell';
import { TimetableTable } from '../components/TimetableTable';
import { HijriCalendarGrid } from '../components/HijriCalendarGrid';
import type { IslamicEvent, PrayerDay } from '../lib/types';
import { fetchWithCache } from '../lib/appCache';
import { prayerMonth } from '../lib/api';
import {
  addDaysISO,
  formatDateLong,
  formatTime,
  isIsoDate,
  monthLabel,
  monthYearFromISO,
  normalizeISODate,
  todayISO
} from '../lib/time';
import { readJson, writeJson } from '../lib/storage';
import { getEffectiveHijriAdjustment, getEffectiveSettings } from '../lib/hijriRegional';
import { CALC_METHODS } from '../lib/constants';
import type { PrayerKey, NextPrayerInfo } from '../lib/nextPrayer';
import { computeNextPrayer } from '../lib/nextPrayer';
import type { PrayerPhaseState } from '../lib/prayerPhase';
import { computePrayerPhaseState } from '../lib/prayerPhase';
import { applyHijriAdjustmentToPrayerDays, deriveUpcomingIslamicEvents } from '../lib/hijriEngine';
import { useLocationState } from '../lib/useLocationState';
import { useSettingsState } from '../lib/useSettingsState';
import { PrayerRing } from '../components/PrayerRing';
import { DaysArc } from '../components/DaysArc';
import { PrayerWindowsPanel, readWindowsMadhab } from '../components/PrayerWindowsPanel';
import type { Madhab } from '../lib/prayerWindows';
import { LOCALES, LOCALE_LABELS, useLocale, hijriLabel, eventName, calcMethodLabel, SUN_LABELS, fmt } from '../lib/i18n';

/* ── helpers ─────────────────────────────────────────────── */

function calcRamadanTable(days: PrayerDay[]) {
  return days
    .filter((d) => d.dateHijri.month === 9)
    .sort((a, b) => a.dateGregorianISO.localeCompare(b.dateGregorianISO))
    .map((d, idx) => ({
      ramadanDayNumber: idx + 1,
      dateGregorianISO: d.dateGregorianISO,
      hijri: d.dateHijri,
      suhoor: d.timings.fajr,
      iftar: d.timings.maghrib,
      dhuhr: d.timings.dhuhr,
      asr: d.timings.asr,
      isha: d.timings.isha
    }));
}

function deriveWeekDays(days: PrayerDay[], anchorISO: string): PrayerDay[] {
  const wanted = new Set(Array.from({ length: 7 }, (_, idx) => addDaysISO(anchorISO, idx)));
  return days.filter((d) => wanted.has(d.dateGregorianISO)).sort((a, b) => a.dateGregorianISO.localeCompare(b.dateGregorianISO));
}

const PRAYER_ORDER: Array<{ k: keyof PrayerDay['timings']; hint?: boolean }> = [
  { k: 'fajr' },
  { k: 'sunrise', hint: true },
  { k: 'dhuhr' },
  { k: 'asr' },
  { k: 'maghrib' },
  { k: 'isha' }
];

type TimetableTab = 'daily' | 'weekly' | 'monthly' | 'ramadan' | 'calendar';

/* ── small SVG icons ─────────────────────────────────────── */

function CaretIcon({ open }: { open: boolean }) {
  return (
    <svg
      width="11"
      height="11"
      viewBox="0 0 10 10"
      fill="none"
      style={{ transition: 'transform .2s ease', transform: open ? 'rotate(180deg)' : 'rotate(0deg)', opacity: 0.7 }}
    >
      <path d="M2 4l3 3 3-3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function LocationIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
      <path d="M8 14s5-4.5 5-8.5A5 5 0 1 0 3 5.5C3 9.5 8 14 8 14z" stroke="currentColor" strokeWidth="1.4" />
      <circle cx="8" cy="5.5" r="1.6" fill="currentColor" />
    </svg>
  );
}

function CalendarIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
      <rect x="2" y="3" width="12" height="11" rx="1.5" stroke="currentColor" strokeWidth="1.3" />
      <path d="M2 6h12M5 2v3M11 2v3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  );
}

function RefreshIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
      <path d="M14 8a6 6 0 1 1-1.76-4.24M14 3v3h-3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function InfoIcon() {
  return (
    <svg width="11" height="11" viewBox="0 0 16 16" fill="none">
      <circle cx="8" cy="8" r="6.3" stroke="currentColor" strokeWidth="1.3" />
      <path d="M8 7.2v4M8 5.2v.6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}

function MoonStarIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <path d="M16 5a8 8 0 1 0 4 9 6.5 6.5 0 0 1-9-6 6.5 6.5 0 0 1 5-6z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
      <path d="M17 7l.7 1.8L19.5 9l-1.8.7L17 11.5l-.7-1.8L14.5 9l1.8-.2L17 7z" fill="currentColor" />
    </svg>
  );
}

/* ── TopStrip ─────────────────────────────────────────────── */

function TopStrip(props: {
  location: import('../lib/types').Location | null;
  day: PrayerDay | null;
  activeDate: string;
  timeZone?: string;
  settings: import('../lib/types').Settings;
  onSettingsChange: (s: import('../lib/types').Settings) => void;
  onLocationChange: (loc: import('../lib/types').Location) => void;
  onDateChange: (d: string) => void;
  onStepDay: (delta: number) => void;
  onToday: () => void;
  onRefresh: () => void;
  busy: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const { location, day, settings } = props;
  const { t, locale } = useLocale();

  const hijriText = day ? hijriLabel(locale, day.dateHijri) : '';
  const gregLabel = formatDateLong(props.activeDate, props.timeZone, locale);

  return (
    <div className="ppTopStrip">
      <button className="ppTopStripBtn" onClick={() => setExpanded((v) => !v)} aria-expanded={expanded}>
        <div className="ppTopStripLeft">
          <span className="ppLocPin" aria-hidden="true"><LocationIcon /></span>
          <span className="ppLoc">{location?.city ?? t.selectCityShort}</span>
          {hijriText && (
            <>
              <span className="ppLocSep">·</span>
              <span className="ppHijri">{hijriText}</span>
            </>
          )}
          <span className="ppLocSep">·</span>
          <span className="ppMuted">{gregLabel}</span>
        </div>
        <CaretIcon open={expanded} />
      </button>

      {expanded && (
        <div className="ppTopStripPanel">
          <div className="ppTopStripPanelRow">
            <label className="ppFld">
              <span className="ppFldLabel">{t.locationLabel}</span>
              <div className="ppFldInput" style={{ display: 'flex', gap: 6, alignItems: 'stretch' }}>
                <div style={{ flex: 1 }}>
                  <CityPicker value={location} onChange={props.onLocationChange} />
                </div>
              </div>
            </label>

            <label className="ppFld">
              <span className="ppFldLabel">{t.dateLabel}</span>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <button className="btn" onClick={() => props.onStepDay(-1)} aria-label={t.prevDayAria}>‹</button>
                <input className="input" type="date" value={props.activeDate} onChange={(e) => props.onDateChange(e.target.value)} style={{ width: 160 }} />
                <button className="btn" onClick={() => props.onStepDay(1)} aria-label={t.nextDayAria}>›</button>
                <button className="btn" onClick={props.onToday}>{t.today}</button>
              </div>
            </label>
          </div>

          <div className="ppTopStripPanelRow">
            <label className="ppFld">
              <span className="ppFldLabel">{t.timeFormat}</span>
              <div className="ppSeg">
                <button className={`ppSegBtn ${settings.timeFormat === '24h' ? 'on' : ''}`} onClick={() => props.onSettingsChange({ ...settings, timeFormat: '24h' })}>24h</button>
                <button className={`ppSegBtn ${settings.timeFormat === '12h' ? 'on' : ''}`} onClick={() => props.onSettingsChange({ ...settings, timeFormat: '12h' })}>12h</button>
              </div>
            </label>
            <label className="ppFld">
              <span className="ppFldLabel">{t.calculation}</span>
              <div className="ppFldInput">
                <select className="input" value={String(settings.methodId)} onChange={(e) => props.onSettingsChange({ ...settings, methodId: Number(e.target.value) })}>
                  {CALC_METHODS.map((m) => <option key={m.id} value={String(m.id)}>{calcMethodLabel(locale, m.id, m.label)}</option>)}
                </select>
              </div>
            </label>
            <label className="ppFld">
              <span className="ppFldLabel">{t.asrMadhhab}</span>
              <div className="ppSeg">
                <button className={`ppSegBtn ${settings.madhhab === 'shafi' ? 'on' : ''}`} onClick={() => props.onSettingsChange({ ...settings, madhhab: 'shafi' })}>{t.shafi}</button>
                <button className={`ppSegBtn ${settings.madhhab === 'hanafi' ? 'on' : ''}`} onClick={() => props.onSettingsChange({ ...settings, madhhab: 'hanafi' })}>{t.hanafi}</button>
              </div>
            </label>
          </div>

          <div className="ppTopStripPanelRow">
            <label className="ppFld">
              <span className="ppFldLabel">{t.fajrFard}</span>
              <div className="ppFldInput" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <input
                  className="input"
                  type="number"
                  min={0}
                  max={90}
                  step={1}
                  inputMode="numeric"
                  value={settings.fardBuffers.fajr}
                  onChange={(e) => props.onSettingsChange({ ...settings, fardBuffers: { ...settings.fardBuffers, fajr: Math.max(0, Math.min(90, Number(e.target.value) || 0)) } })}
                  style={{ width: 90 }}
                  aria-label="Minutes after Fajr adhan to mark fard completed"
                />
                <span className="ppMuted ppSmall">{t.minAfterAdhan}</span>
              </div>
            </label>
            <label className="ppFld">
              <span className="ppFldLabel">{t.asrFard}</span>
              <div className="ppFldInput" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <input
                  className="input"
                  type="number"
                  min={0}
                  max={90}
                  step={1}
                  inputMode="numeric"
                  value={settings.fardBuffers.asr}
                  onChange={(e) => props.onSettingsChange({ ...settings, fardBuffers: { ...settings.fardBuffers, asr: Math.max(0, Math.min(90, Number(e.target.value) || 0)) } })}
                  style={{ width: 90 }}
                  aria-label="Minutes after Asr adhan to mark fard completed"
                />
                <span className="ppMuted ppSmall">{t.minAfterAdhan}</span>
              </div>
            </label>
          </div>

          <div className="ppTopStripFootnote">
            <span>{t.sourceAladhan}</span>
            <span>·</span>
            <span>{t.timezoneLabel}: {location?.timezone ?? '—'}</span>
            <span>·</span>
            <span>{t.updatedLabel} {day?.meta.lastUpdatedISO ? new Date(day.meta.lastUpdatedISO).toLocaleString(locale === 'en' ? undefined : `${locale}-u-nu-latn`) : '—'}</span>
            <span style={{ flex: 1 }} />
            <button className="ppLinklike" onClick={props.onRefresh} disabled={props.busy}>{props.busy ? t.refreshing : t.refresh}</button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Hero with large countdown ────────────────────────────── */

function HeroSection(props: {
  day: PrayerDay;
  nextDay: PrayerDay | null;
  timeZone: string;
  settings: import('../lib/types').Settings;
  onPhaseChange: (info: NextPrayerInfo, phase: PrayerPhaseState) => void;
}) {
  const { t, locale, prayerName } = useLocale();
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const id = window.setInterval(() => setTick((t) => t + 1), 1000);
    return () => window.clearInterval(id);
  }, []);

  const info = useMemo(() => {
    void tick;
    return computeNextPrayer({ day: props.day, nextDay: props.nextDay, timeZone: props.timeZone });
  }, [props.day, props.nextDay, props.timeZone, tick]);

  const phase = useMemo(() => {
    return computePrayerPhaseState({ day: props.day, nextDay: props.nextDay, timeZone: props.timeZone, info: info! });
  }, [props.day, props.nextDay, props.timeZone, info]);

  const onPhaseChange = props.onPhaseChange;
  useEffect(() => {
    if (info) onPhaseChange(info, phase);
  }, [info, phase, onPhaseChange]);

  if (!info) return null;

  const inSec = Math.max(0, info.next.inSeconds);
  const total = 60 * 60 * 6;
  const p = 1 - Math.min(1, inSec / total);
  const hh = Math.floor(inSec / 3600);
  const mm = Math.floor((inSec % 3600) / 60);
  const ss = inSec % 60;
  const pad = (n: number) => String(n).padStart(2, '0');

  const atTime = formatTime(info.next.atLocal, props.settings.timeFormat);
  const methodName = props.day.meta.methodName;
  const madhhab = props.day.meta.madhhab === 'hanafi' ? t.hanafi : t.shafi;
  const nextName = prayerName(info.next.key);
  const currentName = info.current ? prayerName(info.current.key) : '—';
  const sunText = SUN_LABELS[locale][phase.sun.label] ?? phase.sun.label;

  return (
    <div className="ppHero">
      <div className="ppHeroLeft">
        <PrayerRing
          progress={p}
          size={224}
          labelTop={t.upNext}
          labelMain={nextName}
          labelSub={`${t.atWord} ${atTime}`}
          breathing
          intensity={phase.animationIntensity}
        />
      </div>

      <div className="ppHeroRight">
        <div className="ppHeroEyebrow">{t.timeUntilNext}</div>
        <div className="ppCountdown">
          <span className="ppCdNum">{pad(hh)}</span>
          <span className="ppCdSep">:</span>
          <span className="ppCdNum">{pad(mm)}</span>
          <span className="ppCdSep">:</span>
          <span className="ppCdNum ppCdSec">{pad(ss)}</span>
        </div>
        <div className="ppHeroSubline">
          <span>{t.current}: <strong>{currentName}</strong></span>
          <span className="ppDot">·</span>
          <span>{t.nextPrefix}: <strong>{nextName}</strong> {t.atWord} <strong>{atTime}</strong></span>
        </div>
        <div className="ppHeroChips">
          <span className="chip soft">{sunText}</span>
          <span className="chip soft">{methodName}</span>
          <span className="chip soft">{t.asrPrefix}: {madhhab}</span>
        </div>
      </div>
    </div>
  );
}

/* ── Prayer strip (6 cells) ──────────────────────────────── */

function PrayerStrip(props: {
  day: PrayerDay;
  timeFormat: '24h' | '12h';
  currentKey?: PrayerKey;
  nextKey?: PrayerKey;
}) {
  const { t, prayerName } = useLocale();
  return (
    <div className="ppStrip">
      {PRAYER_ORDER.map((it) => {
        const isCurrent = props.currentKey === it.k;
        const isNext = props.nextKey === it.k;
        const cls = ['ppStripCell', isCurrent && 'ppIsCurrent', isNext && 'ppIsNext', it.hint && 'ppIsMeta'].filter(Boolean).join(' ');
        return (
          <div key={it.k} className={cls}>
            {isCurrent && <span className="ppStripBadge ppBadgeCurrent">{t.badgeCurrent}</span>}
            {isNext && <span className="ppStripBadge ppBadgeNext">{t.badgeUpNext}</span>}
            <div className="ppStripLabel">{prayerName(it.k as string)}</div>
            <div className="ppStripTime">{formatTime(props.day.timings[it.k], props.timeFormat)}</div>
            {it.hint && <div className="ppStripHint">{t.notAPrayer}</div>}
          </div>
        );
      })}
    </div>
  );
}

/* ── Events line (collapsed) ──────────────────────────────── */

function EventsLine(props: { events: IslamicEvent[] }) {
  const { t, locale } = useLocale();
  const [open, setOpen] = useState(false);
  if (!props.events.length) return null;
  const next = props.events[0];
  const daysUntil = (() => {
    const start = new Date(next.approxGregorianRange.startISO);
    const now = new Date();
    return Math.max(0, Math.ceil((start.getTime() - now.getTime()) / 86400000));
  })();

  return (
    <div className="ppEvt">
      <button className="ppEvtHeader" onClick={() => setOpen((v) => !v)}>
        <span className="ppEvtDot" />
        <span className="ppEvtMain">
          <strong>{eventName(locale, next.hijriDate.month, next.hijriDate.day, next.name)}</strong>
          <span className="ppMuted"> {fmt(t.inDays, { n: daysUntil })}</span>
          <span className="ppMuted"> · {next.hijriDate.day}/{next.hijriDate.month}</span>
        </span>
        <span className="ppEvtMore">
          {open ? t.hide : `+${props.events.length - 1} ${t.moreSuffix}`} <CaretIcon open={open} />
        </span>
      </button>
      {open && (
        <div className="ppEvtList">
          {props.events.slice(1).map((e) => {
            const d = Math.max(0, Math.ceil((new Date(e.approxGregorianRange.startISO).getTime() - Date.now()) / 86400000));
            return (
              <div key={e.name} className="ppEvtRow">
                <span className="ppEvtDot ppEvtDotSoft" />
                <span><strong>{eventName(locale, e.hijriDate.month, e.hijriDate.day, e.name)}</strong> <span className="ppMuted">{fmt(t.inDays, { n: d })}</span></span>
                <span className="ppMuted">{e.hijriDate.day}/{e.hijriDate.month}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ── Timetable disclosure ─────────────────────────────────── */

function TimetableSection(props: {
  weekDays: PrayerDay[];
  monthDays: PrayerDay[];
  events: IslamicEvent[];
  activeDate: string;
  timeFormat: '24h' | '12h';
  timeZone?: string;
  year: number;
  month: number;
}) {
  const { t, locale, prayerName } = useLocale();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<TimetableTab>('weekly');
  const ramadanRows = useMemo(() => calcRamadanTable(props.monthDays), [props.monthDays]);
  const tabs: Array<{ k: TimetableTab; l: string }> = [
    { k: 'daily', l: t.tabToday },
    { k: 'weekly', l: t.tabWeek },
    { k: 'monthly', l: t.tabMonth },
    { k: 'ramadan', l: t.tabRamadan },
    { k: 'calendar', l: t.tabCalendar }
  ];

  return (
    <div className="ppTt">
      <button className="ppTtHeader" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
        <div className="ppTtHeaderLeft">
          <span className="ppTtIcon" aria-hidden="true"><CalendarIcon /></span>
          <span>{t.fullTimetable}</span>
          <span className="ppMuted ppSmall">{t.timetableSub}</span>
        </div>
        <CaretIcon open={open} />
      </button>

      {open && (
        <div className="ppTtBody">
          <div className="ppTtTabs">
            {tabs.map((t) => (
              <button key={t.k} className={`ppTtTab ${tab === t.k ? 'on' : ''}`} onClick={() => setTab(t.k)}>{t.l}</button>
            ))}
          </div>

          <div style={{ padding: 0 }}>
            {tab === 'daily' && (
              <div className="ppTtPad ppMuted">
                {t.dailyTabNote}
              </div>
            )}

            {tab === 'weekly' && props.weekDays.length > 0 && (
              <TimetableTable days={props.weekDays} timeFormat={props.timeFormat} />
            )}

            {tab === 'monthly' && props.monthDays.length > 0 && (
              <TimetableTable days={props.monthDays} timeFormat={props.timeFormat} title={monthLabel(props.year, props.month, props.timeZone, locale)} />
            )}

            {tab === 'ramadan' && (
              ramadanRows.length > 0 ? (
                <div className="ppTtTable">
                  <table>
                    <thead>
                      <tr>
                        <th>{t.thDay}</th>
                        <th>{t.thDate}</th>
                        <th>{t.thHijri}</th>
                        <th>{t.thSuhoor}</th>
                        <th>{t.thIftar}</th>
                        <th>{prayerName('dhuhr')}</th>
                        <th>{prayerName('asr')}</th>
                        <th>{prayerName('isha')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ramadanRows.map((r) => (
                        <tr key={r.dateGregorianISO}>
                          <td>{r.ramadanDayNumber}</td>
                          <td>{r.dateGregorianISO}</td>
                          <td className="ppMuted">{hijriLabel(locale, r.hijri)}</td>
                          <td>{formatTime(r.suhoor, props.timeFormat)}</td>
                          <td>{formatTime(r.iftar, props.timeFormat)}</td>
                          <td>{formatTime(r.dhuhr, props.timeFormat)}</td>
                          <td>{formatTime(r.asr, props.timeFormat)}</td>
                          <td>{formatTime(r.isha, props.timeFormat)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="ppTtPad ppMuted">{t.noRamadan}</div>
              )
            )}

            {tab === 'calendar' && props.monthDays.length > 0 && (
              <div style={{ padding: 14 }}>
                <HijriCalendarGrid days={props.monthDays} events={props.events} activeDateISO={props.activeDate} />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Footer note ──────────────────────────────────────────── */

function FooterNote() {
  const { t } = useLocale();
  const [open, setOpen] = useState(false);
  return (
    <div className="ppFtr">
      <button className="ppFtrBtn" onClick={() => setOpen((v) => !v)}>
        <span className="ppFtrInfo" aria-hidden="true"><InfoIcon /></span>
        {t.aboutTheseTimes}
        <CaretIcon open={open} />
      </button>
      {open && (
        <div className="ppFtrBody ppMuted">
          {t.footerAboutBody}
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   Main page
   ══════════════════════════════════════════════════════════════ */

export function PrayerPage() {
  const { location, setLocation } = useLocationState();
  const { settings, setSettings } = useSettingsState();
  const { t, locale, setLocale } = useLocale();

  const [windowsMadhab, setWindowsMadhabState] = useState<Madhab>(() => readWindowsMadhab());
  const setWindowsMadhab = useCallback((m: Madhab) => {
    setWindowsMadhabState(m);
    try { localStorage.setItem('ap_windows_madhab_v1', m); } catch { /* ignore */ }
  }, []);

  const zonedToday = useMemo(() => todayISO(location?.timezone), [location?.timezone]);
  // Always open on "today". A previously persisted active date must not strand the
  // user on a stale past day (which would hide the live hero + day's-arc).
  const [selectedDate, setSelectedDate] = useState<string>(() => todayISO());

  useEffect(() => {
    const normalized = normalizeISODate(selectedDate, zonedToday);
    if (!isIsoDate(selectedDate)) { setSelectedDate(zonedToday); return; }
    if (normalized !== selectedDate) setSelectedDate(normalized);
  }, [selectedDate, zonedToday]);

  const activeDate = useMemo(() => normalizeISODate(selectedDate, zonedToday), [selectedDate, zonedToday]);
  const { year, month } = useMemo(() => monthYearFromISO(activeDate), [activeDate]);
  const effectiveHijriAdjustment = useMemo(() => getEffectiveHijriAdjustment(settings, location), [settings, location]);

  const [monthDaysRaw, setMonthDaysRaw] = useState<PrayerDay[]>(() => readJson<PrayerDay[]>('cached_month', []));
  const [focus, setFocus] = useState<{ current?: PrayerKey; next?: PrayerKey }>({});
  const [atmosphere, setAtmosphere] = useState<PrayerPhaseState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const handlePhaseChange = useCallback((info: NextPrayerInfo, phase: PrayerPhaseState) => {
    setFocus({ current: info?.current?.key, next: info?.next.key });
    setAtmosphere(phase);
  }, []);

  useEffect(() => { writeJson('prayer_active_date', activeDate); }, [activeDate]);

  const monthDays = useMemo(() => applyHijriAdjustmentToPrayerDays(monthDaysRaw, effectiveHijriAdjustment), [monthDaysRaw, effectiveHijriAdjustment]);
  const events = useMemo<IslamicEvent[]>(() => deriveUpcomingIslamicEvents(activeDate, effectiveHijriAdjustment, 10), [activeDate, effectiveHijriAdjustment]);

  const day = useMemo(() => monthDays.find((d) => d.dateGregorianISO === activeDate) ?? null, [monthDays, activeDate]);
  const nextDay = useMemo(() => monthDays.find((d) => d.dateGregorianISO === addDaysISO(activeDate, 1)) ?? null, [monthDays, activeDate]);
  const weekDays = useMemo(() => deriveWeekDays(monthDays, activeDate), [monthDays, activeDate]);

  async function refreshAll() {
    if (!location) { setError(null); return; }
    if (!location.city || !location.country) { setError(t.selectCity); return; }
    setBusy(true);
    setError(null);
    try {
      const effectiveSettings = getEffectiveSettings(settings, location);
      const monthKey = `month:v4:raw|${year}-${month}|${location.city}|${location.country}|m=${effectiveSettings.methodId}|md=${effectiveSettings.madhhab}|tz=${location.timezone}`;
      const monthRes = await fetchWithCache(
        monthKey,
        () => prayerMonth(location, month, year, { ...settings, hijriAdjustmentMode: 'manual', hijriAdjustment: 0 })
          .then((d) => ({ ok: true as const, data: d.days }))
          .catch((e: unknown) => ({ ok: false as const, error: e instanceof Error ? e.message : 'Failed' })),
        { maxAgeMs: 6 * 60 * 60 * 1000 }
      );
      if (monthRes.result.ok) {
        setMonthDaysRaw(monthRes.result.data);
        writeJson('cached_month', monthRes.result.data);
      } else {
        setError(monthRes.result.error);
      }
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    if (!location) return;
    void refreshAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location?.city, location?.country, location?.timezone, settings.calculationMethodMode, settings.methodId, settings.madhhab, year, month]);

  const isToday = activeDate === zonedToday;

  return (
    <AmbientShell state={atmosphere}>
      <div className="ppInner" style={{ maxWidth: 1100, margin: '0 auto', padding: '18px 22px 22px' }}>
        {/* Header */}
        <div className="ppHeader">
          <div className="brand">
            <span className="brandMark" aria-hidden="true"><MoonStarIcon /></span>
            <span className="brandName">{t.brandPrayer}</span>
          </div>
          <div className="row" style={{ gap: 6 }}>
            <div className="apLangSwitch" role="group" aria-label={t.languageAria}>
              {LOCALES.map((l) => (
                <button key={l} className={`apLangBtn ${locale === l ? 'on' : ''}`} onClick={() => setLocale(l)} aria-pressed={locale === l}>
                  {LOCALE_LABELS[l]}
                </button>
              ))}
            </div>
            <button className="btn" style={{ padding: '8px 9px', borderRadius: 12 }} onClick={() => void refreshAll()} disabled={busy} title={t.refresh}>
              <RefreshIcon />
            </button>
          </div>
        </div>

        {/* Top strip */}
        <TopStrip
          location={location}
          day={day}
          activeDate={activeDate}
          timeZone={location?.timezone}
          settings={settings}
          onSettingsChange={setSettings}
          onLocationChange={setLocation}
          onDateChange={(v) => setSelectedDate(normalizeISODate(v, activeDate))}
          onStepDay={(delta) => setSelectedDate((prev) => addDaysISO(normalizeISODate(prev, activeDate), delta))}
          onToday={() => setSelectedDate(zonedToday)}
          onRefresh={() => void refreshAll()}
          busy={busy}
        />

        {error && (
          <div className="card" style={{ padding: 14, borderColor: 'var(--danger-border)' }}>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>{t.couldntRefresh}</div>
            <div className="ppMuted" style={{ fontSize: 13 }}>{error}</div>
          </div>
        )}

        {/* Hero */}
        {day && location && isToday ? (
          <HeroSection
            day={day}
            nextDay={nextDay}
            timeZone={location.timezone}
            settings={settings}
            onPhaseChange={handlePhaseChange}
          />
        ) : null}

        {/* Prayer strip */}
        {day ? (
          <PrayerStrip day={day} timeFormat={settings.timeFormat} currentKey={isToday ? focus.current : undefined} nextKey={isToday ? focus.next : undefined} />
        ) : (
          <div className="card" style={{ padding: 14 }}>
            <div className="skeleton" style={{ height: 80, width: '100%' }} />
          </div>
        )}

        {/* The day's arc — sun/moon visualization + makruh/forbidden windows */}
        {day && location ? (
          <DaysArc
            timings={day.timings}
            timeFormat={settings.timeFormat}
            timeZone={location.timezone}
            warnMadhab={windowsMadhab}
            fardBuffers={settings.fardBuffers}
            isToday={isToday}
          />
        ) : null}

        {/* Per-madhab makruh/forbidden prayer windows + detailed rulings */}
        {day ? (
          <PrayerWindowsPanel
            timings={day.timings}
            timeFormat={settings.timeFormat}
            fardBuffers={settings.fardBuffers}
            madhab={windowsMadhab}
            onMadhabChange={setWindowsMadhab}
          />
        ) : null}

        {/* Events */}
        {events.length > 0 && <EventsLine events={events} />}

        {/* Timetable */}
        <TimetableSection
          weekDays={weekDays}
          monthDays={monthDays}
          events={events}
          activeDate={activeDate}
          timeFormat={settings.timeFormat}
          timeZone={location?.timezone}
          year={year}
          month={month}
        />

        {/* Footer */}
        <FooterNote />
      </div>
    </AmbientShell>
  );
}
